/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { OptionList } from '@atoms';
import {
  buildTasOptionsByType,
  getTasDependentFieldUpdates,
  getTasRuleState,
  RulesTASRelational,
} from '@features/offers/utils/tasRelational';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastMessage } from '@molecules';
import { formatCurrencyBRL, parseCurrency } from '@utils/currency.util';
import { Modal } from 'antd';
import { isAfter, isBefore, isValid } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { offerApi } from '../apis/offerApis';
import {
  ACQUISITION_TYPE_CODES,
  CHECKOUT_OFFER_TYPE_CODE,
  MINIMUM_WARRANTY_DAYS,
  SEASONALITY_PERIODS,
} from '../constants';
import { OfferFormData } from '../interfaces/offersInterface';
import {
  createPayloadFromFormData,
  UploadedFields,
} from '../utils/createPayloadFromFormData';
import { formOfferFactory } from '../utils/formOfferFactory';
import { buildOfferCheckoutLink } from '../utils/helpers';
import { coerceToDate, displayLocal, ts } from '../utils/normalizeDate';
import { useOfferProperties } from './useOfferProperties';
import { useOfferUpsellFlowOptions } from './useOfferUpsellFlowOptions';
import { useProductById } from './useProductById';

const COUNTER_INITIAL_VALUES: Pick<OfferFormData, 'counter'> = {
  counter: {
    typeCounter: 'offerCounter',
    offerCounter: {
      bgCounter: '',
      rightImg: '',
      rightImgMobile: '',
      leftImg: '',
      leftImgMobile: '',
    },
    uniqueVisitCounter: {
      timeoutRedirectUrl: '',
      timerMinute: '',
      timerSecond: '',
      countdownPhrase: '',
      cacheExpirationTime: '',
    },
    multiCounter: {
      bgCounter: '',
      rightImg: '',
      rightImgMobile: '',
      leftImg: '',
      leftImgMobile: '',
      schedules: [],
    },
  },
};

const DEFAULT_VALUES: OfferFormData = {
  offerName: '',
  accessProfileId: '',
  offerDescription: '',
  bannerWeb: '',
  bannerMobile: '',
  bannerSideWeb: '',
  logo: '',
  sensitiveData: {
    acquisition: '',
    seasonality: '',
    type: '',
    fullPrice: '',
    hasRetryDiscounts: false,
    retryDiscounts: [],
    installments: '',
    displayPrice: '',
    startDate: '',
    endDate: '',
    purchaseBtnLabel: '',
    offerStandard: false,
    isMailPromotional: false,
    renewalDisabled: false,
    renewalOfferId: '',
    disclaimer: '',
    privacyPolicyRef: 'https://www.queimadiaria.com/politica-de-privacidade/',
    termsRef: 'https://www.queimadiaria.com/termos-de-uso/',
    onlyFull: false,
    profileId: '',
    warrantyDays: MINIMUM_WARRANTY_DAYS,
    paymentMethods: ['creditCard', 'nupay'],
    isDisplayCoupon: false,
    flowId: '',
  },
  analytics: {
    googleAnalyticsIds: '',
    facebookPixels: '',
  },
  hasPromotionalOffer: false,
  promotionalOffer: {
    promotionalAmount: '',
    qtdPromotionalDays: '',
  },
  hasCounter: false,
  isPhysicProduct: false,
  physicProduct: {
    isFreeShipping: false,
    primaryColor: '#FF4F00',
    footerText: '',
    seo: {
      title: '',
      smallDescription: '',
      largeDescription: '',
      image: '',
      favicon: '',
      siteName: '',
      keywords: [] as string[],
    },
  },
  isAvailableMobile: false,
  mobile: {
    itunesStoreId: '',
    googleStoreId: '',
  },
  ...COUNTER_INITIAL_VALUES,
};

export default function useFormOffer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { offerId, productId } = useParams();
  const {
    offerProperties,
    acquisitionTypeIdsByCodes,
    checkoutTypeIdsByCodes,
    seasonalityIdsByPeriods,
    rulesTASRelational,
    isLoading: isLoadingProperties,
  } = useOfferProperties();
  const { product } = useProductById(Number(productId));
  const upsellFlowOptions = useOfferUpsellFlowOptions();
  const formType = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('type') as 'edit' | 'view' | 'duplicate' | undefined;
  }, [location]);
  const isEditOrView = useMemo(
    () => formType === 'edit' || formType === 'view',
    [formType]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isOfferStandard, setIsOfferStandard] = useState(false);
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);
  const [isPersistedAsAvailableMobile, setIsPersistedAsAvailableMobile] =
    useState(false);
  const [isPersistedAsPromotionalOffer, setIsPersistedAsPromotionalOffer] =
    useState(false);

  useEffect(() => {
    offerId && getOffers(offerId);
  }, [offerId]);

  async function getOffers(id: string) {
    setIsLoading(true);
    await offerApi.getOfferById(id).then(data => {
      const newOffer = formOfferFactory(data);
      reset(newOffer);
      setIsOfferStandard(!!data.isDefault);
      setIsPersistedAsAvailableMobile(!!newOffer?.isAvailableMobile);
      setIsPersistedAsPromotionalOffer(!!newOffer?.hasPromotionalOffer);
      if (data.flowId) {
        upsellFlowOptions
          .findFlowById(Number(data.flowId))
          .then(flow => {
            setValue('sensitiveData.flowId', flow ? String(flow.id) : '', {
              shouldValidate: true,
            });
          })
          .catch(() => {
            setValue('sensitiveData.flowId', '', {
              shouldValidate: true,
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setValue('sensitiveData.flowId', '', { shouldValidate: true });
        setIsLoading(false);
      }
    });
  }

  const validationSchema = useMemo(
    () =>
      yup.object({
        offerName: yup.string().required('O Nome da oferta é obrigatório'),
        accessProfileId: yup.string(),
        offerDescription: yup
          .string()
          .required('A descrição da oferta é obrigatória'),
        bannerWeb: yup.mixed<any>(),
        bannerMobile: yup.mixed<any>(),
        bannerSideWeb: yup.mixed<any>(),
        logo: yup.mixed<any>(),
        sensitiveData: yup.object({
          fullPrice: yup.string().required('O valor da oferta é obrigatório'),
          displayPrice: yup.string(),
          installments: yup.string().required('O parcelamento é obrigatório'),
          startDate: yup
            .string()
            .required('A data de início é obrigatória')
            .test(
              'is-valid-date-iso',
              'A data de início deve ser uma data válida',
              value => !value || !isNaN(new Date(value).getTime())
            ),
          endDate: yup
            .string()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === '' ? null : value
            )
            .test(
              'min',
              'A data de término deve ser maior que a data de início',
              (value, context) => {
                return (
                  (isOfferStandard && formType !== 'duplicate') ||
                  (!!value && isAfter(value, (context.parent as any).startDate))
                );
              }
            ),
          purchaseBtnLabel: yup.string(),
          isMailPromotional: yup.boolean(),
          renewalDisabled: yup.boolean(),
          renewalOfferId: yup.string(),
          disclaimer: yup.string(),
          acquisition: yup
            .string()
            .test(
              'valid-acquisition',
              'Selecione uma aquisição válida',
              value => {
                if (!value || !offerProperties) return true;

                return !!offerProperties.acquisitionTypes.find(
                  option => option.id.toString() === value
                );
              }
            )
            .required('O tipo de aquisição é obrigatório'),
          seasonality: yup
            .string()
            .test(
              'valid-seasonality',
              'Selecione uma sazonalidade válida',
              value => {
                if (!value || !offerProperties) return true;

                return !!offerProperties.seasonalities.find(
                  option => option.id.toString() === value
                );
              }
            )
            .required('A sazonalidade é obrigatória'),
          type: yup
            .string()
            .test('valid-type', 'Selecione um tipo de venda válido', value => {
              if (!value || !offerProperties) return true;

              return !!offerProperties.types.find(
                option => option.id.toString() === value
              );
            })
            .required('O tipo de venda é obrigatório'),
          privacyPolicyRef: yup
            .string()
            .required('O link para a política de privacidade é obrigatório'),
          termsRef: yup
            .string()
            .required(
              'O link para os termos de responsabilidade é obrigatório'
            ),
          plan: yup.string(),
          offerStandard: yup.boolean(),
          isDisplayCoupon: yup.boolean(),
          flowId: yup.string().test(
            'required-when-editing-default-offer',
            'O funil de upsell é obrigatório',
            value => {
              if (formType === 'edit' && isOfferStandard) return !!value;
              return true;
            }
          ),
          paymentMethods: yup
            .array()
            .of(
              yup
                .string()
                .oneOf(['creditCard', 'nupay', 'pagaleve', 'pix', 'billet'])
            )
            .min(1, 'Selecione ao menos uma forma de pagamento'),
          onlyFull: yup.boolean(),
          hasRetryDiscounts: yup.boolean(),
          retryDiscounts: yup
            .array()
            .when(
              'hasRetryDiscounts',
              ([hasRetryDiscounts], schema, { parent }) => {
                return hasRetryDiscounts === true
                  ? schema.of(
                      yup.object({
                        value: yup
                          .string()
                          .required('O valor múltiplo é obrigatório')
                          .test(
                            'min-value',
                            'O valor deve ser menor que o valor da oferta.',
                            (
                              value: string | undefined,
                              context: yup.TestContext
                            ): boolean => {
                              const path = context.path;
                              const match = path.match(/\[(\d+)\]/);
                              const index = match ? Number(match[1]) : null;

                              if (!value || value === '' || index === null)
                                return false;
                              if (!context?.parent.active) return true;

                              const valueParsed = parseCurrency(value) || 0;

                              return (
                                valueParsed <
                                (parseCurrency(parent.fullPrice) ?? 0)
                              );
                            }
                          ),
                        percentage: yup
                          .string()
                          .required('O percentual é obrigatório')
                          .test(
                            'value-percentage',
                            'O percentual deve ser entre 0 e 100',
                            value => {
                              const valueParsed = parseFloat(value || '0');
                              return valueParsed >= 0 && valueParsed <= 100;
                            }
                          ),
                        isDirectFallback: yup.boolean(),
                        active: yup.boolean(),
                      })
                    )
                  : schema;
              }
            ),
          profileId: yup.string(),
          warrantyDays: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === '' ? undefined : value
            )
            .optional()
            .min(
              MINIMUM_WARRANTY_DAYS,
              `O prazo mínimo de garantia é de ${MINIMUM_WARRANTY_DAYS} dias`
            )
            .test(
              'max-warranty-days',
              'O prazo máximo de garantia é inválido para a sazonalidade selecionada',
              function (value) {
                const { seasonalities } = offerProperties || {};
                const seasonalityId = this.parent.seasonality;
                const selectedSeasonality = seasonalities?.find(
                  option => `${option.id}` === seasonalityId
                );
                if (!selectedSeasonality || !value) return true;
                if (selectedSeasonality.warrantyDays === undefined) return true;
                return value <= selectedSeasonality.period;
              }
            ),
        }),
        analytics: yup.object({
          googleAnalyticsIds: yup.string(),
          facebookPixels: yup.string(),
        }),
        hasPromotionalOffer: yup.boolean(),
        promotionalOffer: yup.object({
          promotionalAmount: yup
            .string()
            .test(
              'required-when-promotional-offer',
              'O valor da oferta promocional é obrigatório',
              function (value) {
                const isPromotionalOffer =
                  this.from?.[1]?.value?.hasPromotionalOffer;
                if (!isPromotionalOffer) return true;

                if (value === undefined || value === null || value === '') {
                  return false;
                }

                const parsedValue = parseCurrency(value);
                return (
                  parsedValue !== undefined &&
                  Number.isFinite(parsedValue) &&
                  parsedValue >= 0
                );
              }
            ),
          qtdPromotionalDays: yup
            .string()
            .test(
              'is-positive-integer',
              'A quantidade de dias deve ser maior do que 0',
              function (value) {
                const isPromotionalOffer =
                  this.from?.[1]?.value?.hasPromotionalOffer;
                if (!isPromotionalOffer) return true;
                if (value === undefined || value === null || value === '') {
                  return true;
                }
                const parsedValue = Number(value);
                return Number.isInteger(parsedValue) && parsedValue > 0;
              }
            )
            .test(
              'required-when-promotional-offer',
              'A quantidade de dias da oferta promocional é obrigatória',
              function (value) {
                const isPromotionalOffer =
                  this.from?.[1]?.value?.hasPromotionalOffer;
                if (!isPromotionalOffer) return true;

                if (value === undefined || value === null || value === '') {
                  return false;
                }

                return true;
              }
            ),
        }),
        isPhysicProduct: yup.boolean(),
        physicProduct: yup.object({
          isFreeShipping: yup.boolean(),
          primaryColor: yup.string(),
          footerText: yup.string(),
          seo: yup.object({
            title: yup.string(),
            smallDescription: yup.string(),
            largeDescription: yup.string(),
            image: yup.string(),
            favicon: yup.string(),
            siteName: yup.string(),
            keywords: yup.array().of(yup.string()),
          }),
        }),
        isAvailableMobile: yup.boolean(),
        mobile: yup.object({
          itunesStoreId: yup
            .string()
            .test(
              'at-least-one',
              'Selecione ao menos um ID de loja',
              function (value) {
                const isAvailableMobile =
                  this.from?.[1]?.value?.isAvailableMobile;
                if (!isAvailableMobile) return true;
                const { googleStoreId } = this.parent;
                return !!(value || googleStoreId);
              }
            ),
          googleStoreId: yup
            .string()
            .test(
              'at-least-one',
              'Selecione ao menos um ID de loja',
              function (value) {
                const isAvailableMobile =
                  this.from?.[1]?.value?.isAvailableMobile;
                if (!isAvailableMobile) return true;
                const { itunesStoreId } = this.parent;
                return !!(value || itunesStoreId);
              }
            ),
        }),
        hasCounter: yup.boolean(),
        counter: yup
          .mixed()
          .test(
            'counter-validation',
            'Dados do contador inválidos',
            function (value) {
              const basePath = this.path || 'counter';
              const parent = (this as any).parent || {};

              // se hasCounter não estiver marcado, valido automaticamente
              if (!parent?.hasCounter) return true;

              const errors: yup.ValidationError[] = [];

              if (!value || typeof value !== 'object') {
                errors.push(
                  new yup.ValidationError(
                    'O contador deve ser um objeto',
                    value,
                    basePath
                  )
                );
                throw new yup.ValidationError(errors);
              }

              const counter = value as any;
              const typeCounter = counter.typeCounter;
              if (!typeCounter) {
                errors.push(
                  new yup.ValidationError(
                    'O tipo do contador é obrigatório',
                    undefined,
                    `${basePath}.typeCounter`
                  )
                );
              }

              // offerCounter
              if (typeCounter === 'offerCounter') {
                const bg = String(counter?.offerCounter?.bgCounter || '');
                if (!/^#[0-9A-Fa-f]{6}$/.test(bg)) {
                  errors.push(
                    new yup.ValidationError(
                      'O campo bgCounter deve ser um código HEX válido iniciado com #',
                      bg,
                      `${basePath}.offerCounter.bgCounter`
                    )
                  );
                }
              }

              // uniqueVisitCounter
              if (typeCounter === 'uniqueVisitCounter') {
                const u = counter?.uniqueVisitCounter || {};
                const url = u.timeoutRedirectUrl;
                if (!url) {
                  errors.push(
                    new yup.ValidationError(
                      'A url de redirecionamento é obrigatória!',
                      url,
                      `${basePath}.uniqueVisitCounter.timeoutRedirectUrl`
                    )
                  );
                } else {
                  try {
                    new URL(url);
                  } catch {
                    errors.push(
                      new yup.ValidationError(
                        'Insira uma url de redirecionamento válida',
                        url,
                        `${basePath}.uniqueVisitCounter.timeoutRedirectUrl`
                      )
                    );
                  }
                }

                if (!u.timerMinute) {
                  errors.push(
                    new yup.ValidationError(
                      'Os minutos do contador são obrigatórios',
                      u.timerMinute,
                      `${basePath}.uniqueVisitCounter.timerMinute`
                    )
                  );
                }
                if (!u.timerSecond) {
                  errors.push(
                    new yup.ValidationError(
                      'Os segundos do contador são obrigatórios',
                      u.timerSecond,
                      `${basePath}.uniqueVisitCounter.timerSecond`
                    )
                  );
                }
                if (!u.countdownPhrase) {
                  errors.push(
                    new yup.ValidationError(
                      'A frase do contador é obrigatória',
                      u.countdownPhrase,
                      `${basePath}.uniqueVisitCounter.countdownPhrase`
                    )
                  );
                }
              }

              // multiCounter
              if (typeCounter === 'multiCounter') {
                const m = counter?.multiCounter || {};
                const bg = String(m.bgCounter || '');
                if (bg === '' || !/^#[0-9A-Fa-f]{6}$/.test(bg)) {
                  errors.push(
                    new yup.ValidationError(
                      'O campo bgCounter deve ser um código HEX válido iniciado com # ou vazio',
                      bg,
                      `${basePath}.multiCounter.bgCounter`
                    )
                  );
                }

                const schedules = Array.isArray(m.schedules) ? m.schedules : [];

                type IntervalInfo = {
                  index: number;
                  startStr: string;
                  endStr: string;
                  start: Date | null;
                  end: Date | null;
                };

                const intervals: IntervalInfo[] = schedules.map(
                  (v: any, idx: number) => {
                    const s = coerceToDate(v.startTime);
                    const e = coerceToDate(v.endTime);
                    return {
                      index: idx,
                      startStr: displayLocal(v.startTime),
                      endStr: displayLocal(v.endTime),
                      start: s,
                      end: e,
                    };
                  }
                );

                for (const it of intervals) {
                  if (!it.start || !isValid(it.start)) {
                    errors.push(
                      new yup.ValidationError(
                        'Data de início inválida',
                        it.startStr,
                        `${basePath}.multiCounter.schedules[${it.index}].startTime`
                      )
                    );
                  }
                  if (!it.end || !isValid(it.end)) {
                    errors.push(
                      new yup.ValidationError(
                        'Data de término inválida',
                        it.endStr,
                        `${basePath}.multiCounter.schedules[${it.index}].endTime`
                      )
                    );
                  }
                }

                // Verificar se endTime é maior que startTime
                for (const it of intervals) {
                  if (
                    it.start &&
                    it.end &&
                    isValid(it.start) &&
                    isValid(it.end)
                  ) {
                    if (it.start.getTime() >= it.end.getTime()) {
                      errors.push(
                        new yup.ValidationError(
                          'O horário de término deve ser maior que o horário de início',
                          it.endStr,
                          `${basePath}.multiCounter.schedules[${it.index}].endTime`
                        )
                      );
                    }
                  }
                }

                // Duplicados exatos
                const dup = new Map<string, IntervalInfo[]>();
                for (const it of intervals) {
                  if (!it.start || !it.end) continue;
                  const key = `${ts(it.start)}|${ts(it.end)}`;
                  dup.set(key, [...(dup.get(key) || []), it]);
                }
                for (const group of dup.values()) {
                  if (group.length > 1) {
                    group.forEach(it => {
                      errors.push(
                        new yup.ValidationError(
                          'Período duplicado',
                          it.startStr,
                          `${basePath}.multiCounter.schedules[${it.index}].startTime`
                        )
                      );
                    });
                  }
                }

                // Verificar sobreposições
                for (let i = 0; i < intervals.length; i++) {
                  const a = intervals[i];
                  if (!a.start || !a.end) continue;

                  for (let j = i + 1; j < intervals.length; j++) {
                    const b = intervals[j];
                    if (!b.start || !b.end) continue;

                    // Verificar se há sobreposição
                    if (
                      (isAfter(a.start, b.start) && isBefore(a.start, b.end)) ||
                      (isAfter(a.end, b.start) && isBefore(a.end, b.end)) ||
                      (isAfter(b.start, a.start) && isBefore(b.start, a.end)) ||
                      (isAfter(b.end, a.start) && isBefore(b.end, a.end)) ||
                      a.startStr === b.startStr ||
                      a.endStr === b.endStr
                    ) {
                      errors.push(
                        new yup.ValidationError(
                          'Período sobrepõe ou coincide com outro período',
                          a.startStr,
                          `${basePath}.multiCounter.schedules[${a.index}].startTime`
                        )
                      );
                    }
                  }
                }
              }

              if (errors.length) {
                throw new yup.ValidationError(errors);
              }

              return true;
            }
          ),
      }) as yup.ObjectSchema<OfferFormData>,
    [offerProperties, isOfferStandard, formType]
  );

  const onSubmit = () => showNotification('submitOffer');

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    resetField,
    setError,
    formState,
    trigger,
  } = useForm<OfferFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  const isFormValid = useMemo(() => {
    return formState.isValid;
  }, [formState.isValid]);

  const [
    isDisplayCoupon,
    hasCounter,
    typeCounter,
    bgCounter,
    hasPromotionalOffer,
    isPhysicProduct,
    physicProductPrimaryColor,
    hasRetryDiscounts,
    multiCounterBgCounter,
    acquisition,
    seasonality,
    type,
    isAvailableMobile,
  ] = watch([
    'sensitiveData.isDisplayCoupon',
    'hasCounter',
    'counter.typeCounter',
    'counter.offerCounter.bgCounter',
    'hasPromotionalOffer',
    'isPhysicProduct',
    'physicProduct.primaryColor',
    'sensitiveData.hasRetryDiscounts',
    'counter.multiCounter.bgCounter',
    'sensitiveData.acquisition',
    'sensitiveData.seasonality',
    'sensitiveData.type',
    'isAvailableMobile',
  ]);

  const tasBaseOptions = useMemo(
    () => buildTasOptionsByType(offerProperties),
    [offerProperties]
  );

  const tasRules = useMemo<RulesTASRelational[]>(() => {
    if (
      product?.subscriptionType !== 'monthly' ||
      !seasonalityIdsByPeriods ||
      !acquisitionTypeIdsByCodes
    )
      return rulesTASRelational;

    return [
      ...rulesTASRelational,
      {
        fromType: 'seasonality',
        fromId: seasonalityIdsByPeriods[SEASONALITY_PERIODS.monthly],
        toType: 'acquisition',
        toInclude: [
          acquisitionTypeIdsByCodes[ACQUISITION_TYPE_CODES.subscription],
        ],
      },
    ];
  }, [
    product?.subscriptionType,
    seasonalityIdsByPeriods,
    acquisitionTypeIdsByCodes,
  ]);

  const tasRuleState = useMemo(
    () =>
      getTasRuleState(
        {
          fullPrice: '',
          acquisitionId: acquisition || '',
          seasonalityId: seasonality || '',
          typeId: type || '',
          isFallbackOffer: false,
        },
        tasBaseOptions,
        isEditOrView,
        tasRules
      ),
    [acquisition, seasonality, type, tasBaseOptions, isEditOrView, tasRules]
  );

  const acquisitionTypeOptions = useMemo<OptionList>(
    () =>
      tasRuleState.optionsByType.acquisition.map(option => ({
        label: `${option.value} - ${option.label}`,
        value: option.value,
      })),
    [tasRuleState.optionsByType.acquisition]
  );
  const seasonalityOptions = useMemo<OptionList>(
    () =>
      tasRuleState.optionsByType.seasonality.map(option => ({
        label: `${option.value} - ${option.label}`,
        value: option.value,
      })),
    [tasRuleState.optionsByType.seasonality]
  );
  const typeOptions = useMemo<OptionList>(
    () =>
      tasRuleState.optionsByType.offerType.map(option => ({
        label: `${option.value} - ${option.label}`,
        value: option.value,
      })),
    [tasRuleState.optionsByType.offerType]
  );

  useEffect(() => {
    if (
      !hasInitializedDefaults &&
      !isLoadingProperties &&
      typeOptions.length > 0 &&
      acquisitionTypeOptions.length > 0 &&
      seasonalityOptions.length > 0 &&
      offerProperties &&
      !offerId
    ) {
      setValue(
        'sensitiveData.acquisition',
        `${acquisitionTypeIdsByCodes?.[ACQUISITION_TYPE_CODES.subscription]}`
      );
      setValue(
        'sensitiveData.seasonality',
        `${seasonalityIdsByPeriods?.[SEASONALITY_PERIODS.annual]}`
      );
      setValue(
        'sensitiveData.type',
        `${checkoutTypeIdsByCodes?.[CHECKOUT_OFFER_TYPE_CODE]}`
      );
      setHasInitializedDefaults(true);
    }
  }, [
    hasInitializedDefaults,
    isLoadingProperties,
    offerProperties,
    typeOptions,
    acquisitionTypeOptions,
    seasonalityOptions,
    offerId,
    DEFAULT_VALUES,
    reset,
  ]);

  useEffect(() => {
    if (isEditOrView || !offerProperties) return;

    getTasDependentFieldUpdates(
      {
        fullPrice: '',
        acquisitionId: acquisition || '',
        seasonalityId: seasonality || '',
        typeId: type || '',
        isFallbackOffer: false,
      },
      tasRuleState
    ).forEach(({ fieldName, value }) => {
      const fieldMap = {
        acquisitionId: 'sensitiveData.acquisition',
        seasonalityId: 'sensitiveData.seasonality',
        typeId: 'sensitiveData.type',
      } as const;

      setValue(fieldMap[fieldName], value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, [
    acquisition,
    isEditOrView,
    offerProperties,
    seasonality,
    setValue,
    tasRuleState,
    type,
  ]);

  const handleUpdateRetryDiscountValue = useCallback(
    (index: number, type: string, value: string) => {
      if (type === 'value') {
        const fullPriceValue = parseCurrency(
          getValues('sensitiveData.fullPrice')
        );
        const percentageValue =
          ((Number(fullPriceValue) - (parseCurrency(value) || 0)) /
            Number(fullPriceValue)) *
          100;
        setValue(
          `sensitiveData.retryDiscounts.${index}.percentage`,
          `${+percentageValue.toFixed(2)}`
        );
        trigger(`sensitiveData.retryDiscounts.${index}.percentage`);
      }
      if (type === 'percentage') {
        const fullPriceValue = parseCurrency(
          getValues('sensitiveData.fullPrice')
        );
        const finalValue =
          Number(fullPriceValue) -
          (Number(fullPriceValue) * Number(value)) / 100;
        setValue(
          `sensitiveData.retryDiscounts.${index}.value`,
          `${formatCurrencyBRL(finalValue)}`
        );
        trigger(`sensitiveData.retryDiscounts.${index}.value`);
      }
    },
    [getValues, setValue, trigger]
  );

  const handleUpdateFullPrice = useCallback(
    (value: string) => {
      const fullPriceValue = parseCurrency(value);
      if (!fullPriceValue || fullPriceValue <= 0) return;

      const retryDiscounts = getValues('sensitiveData.retryDiscounts') ?? [];

      retryDiscounts.forEach((retryDiscount, index) => {
        handleUpdateRetryDiscountValue(
          index,
          retryDiscount.typeDiscount,
          retryDiscount.typeDiscount === 'value'
            ? retryDiscount.value
            : retryDiscount.percentage
        );
      });
    },
    [getValues, setValue, trigger]
  );

  const handleApplyCalculatedPrice = useCallback(
    (field: 'fullPrice' | 'displayPrice', value: string) => {
      const fieldPath = `sensitiveData.${field}` as const;

      setValue(fieldPath, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      if (field === 'fullPrice') {
        handleUpdateFullPrice(value);
      }
    },
    [handleUpdateFullPrice, setValue]
  );

  useEffect(() => {
    if (isEditOrView) return;

    const selectedSeasonality = offerProperties?.seasonalities.find(
      option => `${option.id}` === seasonality
    );
    if (!selectedSeasonality) return;

    setValue(
      'sensitiveData.installments',
      String(selectedSeasonality.installments)
    );

    // Don't override warrantyDays if we're in edit/view mode and already have a value
    const currentWarrantyDays = getValues('sensitiveData.warrantyDays');
    if (!currentWarrantyDays) {
      setValue(
        'sensitiveData.warrantyDays',
        selectedSeasonality.warrantyDays ?? MINIMUM_WARRANTY_DAYS
      );
    }
  }, [offerProperties, seasonality, isEditOrView, getValues, setValue]);

  useEffect(() => {
    const selectedAcquisitionType = offerProperties?.acquisitionTypes.find(
      option => `${option.id}` === acquisition
    );
    if (!selectedAcquisitionType) return;
    if (!formType) {
      setValue(
        'sensitiveData.renewalDisabled',
        selectedAcquisitionType.renewalDisabled
      );
      setValue('sensitiveData.onlyFull', selectedAcquisitionType.onlyFull);
    }

    if (hasPromotionalOffer) setValue('sensitiveData.renewalDisabled', false);

    if (
      (selectedAcquisitionType.code === ACQUISITION_TYPE_CODES.purchase ||
        selectedAcquisitionType.code === ACQUISITION_TYPE_CODES.rent) &&
      !hasPromotionalOffer
    ) {
      setValue('sensitiveData.renewalDisabled', true);
    }
    if (
      selectedAcquisitionType.code === ACQUISITION_TYPE_CODES.subscription &&
      formState.isDirty
    ) {
      setValue('sensitiveData.renewalDisabled', false);
    }

    if (!product) return;
    if (product.subscriptionType === 'monthly') {
      setValue(
        'sensitiveData.seasonality',
        `${seasonalityIdsByPeriods?.[SEASONALITY_PERIODS.monthly]}`
      );
      setValue('sensitiveData.installments', '1');
      setValue('sensitiveData.onlyFull', true);
    }
    if (product.isDirectFallback) setValue('sensitiveData.onlyFull', false);
  }, [offerProperties, acquisition, hasPromotionalOffer, product]);

  useEffect(() => {
    if (!hasPromotionalOffer) {
      setValue('promotionalOffer.promotionalAmount', '');
      setValue('promotionalOffer.qtdPromotionalDays', '');
    }
  }, [hasPromotionalOffer]);

  useEffect(() => {
    if (hasCounter === false)
      setValue('counter', COUNTER_INITIAL_VALUES.counter);
  }, [hasCounter]);

  useEffect(() => {
    if (isAvailableMobile === false) {
      setValue('mobile.itunesStoreId', '');
      setValue('mobile.googleStoreId', '');
    }
  }, [isAvailableMobile]);

  useEffect(() => {
    if (typeCounter === 'uniqueVisitCounter') {
      setValue(
        'counter.offerCounter',
        COUNTER_INITIAL_VALUES.counter.offerCounter
      );
      setValue(
        'counter.multiCounter',
        COUNTER_INITIAL_VALUES.counter.multiCounter
      );
    }
    if (typeCounter === 'offerCounter') {
      setValue(
        'counter.uniqueVisitCounter',
        COUNTER_INITIAL_VALUES.counter.uniqueVisitCounter
      );
      setValue(
        'counter.multiCounter',
        COUNTER_INITIAL_VALUES.counter.multiCounter
      );
    }
    if (typeCounter === 'multiCounter') {
      setValue(
        'counter.uniqueVisitCounter',
        COUNTER_INITIAL_VALUES.counter.uniqueVisitCounter
      );
      setValue(
        'counter.offerCounter',
        COUNTER_INITIAL_VALUES.counter.offerCounter
      );
    }
  }, [typeCounter]);

  const handleUploadErrors = useCallback(
    async (uploadedFields: UploadedFields) => {
      showNotification('error');
      for (const errorField of uploadedFields.error) {
        resetField(errorField as any);
        setError(errorField as any, {
          type: 'manual',
        });
      }
      for (const successField of uploadedFields.success) {
        setValue(successField.field as any, successField.value);
      }
    },
    [setValue]
  );

  const submitEdit = useCallback(async () => {
    if (!productId || !offerId) return;
    setIsLoading(true);
    const { offerPayload, uploadedFields } = await createPayloadFromFormData(
      productId,
      getValues()
    );
    if (uploadedFields.error.length > 0) {
      await handleUploadErrors(uploadedFields);
      setIsLoading(false);
      return;
    }
    await offerApi
      .putOfferById(productId, offerId, offerPayload)
      .then(data => {
        data.data ? showNotification('success') : showNotification('error');
      })
      .finally(() => setIsLoading(false));
  }, [productId, offerId, getValues, handleUploadErrors]);

  const submitCreate = useCallback(async () => {
    if (!productId) return;
    setIsLoading(true);
    const { offerPayload, uploadedFields } = await createPayloadFromFormData(
      productId,
      getValues()
    );
    if (uploadedFields.error.length > 0) {
      await handleUploadErrors(uploadedFields);
      setIsLoading(false);
      return;
    }
    await offerApi
      .postOfferById(productId, offerPayload)
      .then(data => {
        data.data ? showNotification('success') : showNotification('error');
      })
      .finally(() => setIsLoading(false));
  }, [productId, getValues, handleUploadErrors]);

  const showNotification = useCallback(
    (type: 'submitOffer' | 'success' | 'error') => {
      const modalPropsByType = {
        submitOffer: () => {
          if (formType === 'edit') {
            submitEdit();
          } else {
            Modal.warning({
              title: 'Atenção!',
              content:
                'Você está prestes a criar uma nova oferta. Por favor, verifique se os dados estão corretos antes de prosseguir, pois essa ação não poderá ser desfeita ou excluída posteriormente.',
              cancelText: 'Cancelar',
              onOk: () => submitCreate(),
              okCancel: true,
            });
          }
        },
        success: () => {
          ToastMessage({
            content: 'Oferta salva com sucesso!',
            type: 'success',
          });
          navigate(`/products/${productId}/offers`);
        },
        error: () =>
          ToastMessage({
            content:
              'Não foi possível realizar a sua solicitação, tente editar a oferta para ver os campos que foram criados corretamente!',
            type: 'error',
          }),
      };
      modalPropsByType[type]();
    },
    [formType, navigate, productId, submitEdit, submitCreate]
  );

  const canCopyOfferLink = useMemo(
    () => isEditOrView && !!productId && !!offerId,
    [isEditOrView, offerId, productId]
  );

  const handleCopyOfferLink = useCallback(() => {
    if (!productId || !offerId) return;
    void navigator.clipboard.writeText(
      buildOfferCheckoutLink(productId, offerId)
    );
  }, [offerId, productId]);

  return {
    onSubmit,
    control,
    handleSubmit,
    formType,
    isEditOrView,
    productId,
    offerId,
    product,
    isLoading,
    isOfferStandard,
    isDisplayCoupon,
    hasCounter,
    typeCounter,
    bgCounter,
    multiCounterBgCounter,
    acquisitionTypeIdsByCodes,
    acquisitionTypeOptions,
    seasonalityOptions,
    typeOptions,
    tasFieldDisabled: {
      acquisition: tasRuleState.disabledByType.acquisition,
      seasonality: tasRuleState.disabledByType.seasonality,
      type: tasRuleState.disabledByType.offerType,
    },
    hasPromotionalOffer,
    hasRetryDiscounts,
    handleApplyCalculatedPrice,
    handleUpdateFullPrice,
    handleUpdateRetryDiscountValue,
    isPhysicProduct,
    physicProductPrimaryColor,
    acquisition,
    isFormValid,
    isAvailableMobile,
    isPersistedAsAvailableMobile,
    isPersistedAsPromotionalOffer,
    upsellFlowOptions,
    canCopyOfferLink,
    handleCopyOfferLink,
  };
}
